import Raven from 'raven-js'
import { Message } from 'element-ui'
import * as log from 'loglevel'

import apollo from '@/graphql'
import router from '@/router'

// GraphQL Queries/Mutations
import ExchangeMFACode from '@/graphql/auth/ExchangeMFACode.gql'
import Login from '@/graphql/auth/Login.gql'
import Logout from '@/graphql/auth/Logout.gql'
import RequestPasswordReset from '@/graphql/auth/RequestPasswordReset.gql'
import ResetPassword from '@/graphql/auth/ResetPassword.gql'

import { LocalStorageHelper } from './auth.helpers'
import { AUTH_EXP_CHECK_INTERVAL } from './auth.constants'

/**
 * Restore previous sessions, perform
 * redirections if necessary.
 *
 * Automatically called on first load.
 */

function init({ dispatch }) {
  dispatch('restoreUser')
  dispatch('checkExpiration')
}

/**
 *         .
 *        ":"
 *      __:____     |"\/"|
 *   ,'        `.    \  /
 *  |  O         \___/  |
 * ~^~^~^~^~^~^~^~^~^~^~^~^~
 * This function is whale-y important!
 *
 * Handles the login view and redirects for
 * various parts of the MFA process.
 *
 * Note: there is a case when login fails
 * client-side but succeeds server-side--
 * in such a case, cookies are set but no
 * user exists in localStorage. This function
 * does its best to identify those cases and
 * prevent the user from interacting with the
 * dashboard when no user is in localStorage.
 */
function login({ commit, dispatch }, { email, password, $route }) {
  commit('setLoading', true)
  commit('setStatus', 'LOGGING_IN')
  apollo
    .defaultClient
    .mutate({
      mutation: Login,
      variables: { email, password },
    })
    .then(({ data: { login: { code, user, iat, exp } } }) => {
      commit('setStatus', code)
      if (code === 'WRONG_CREDENTIALS') {
        log.debug('Wrong credentials')
        commit('setLoading', false)
      }
      if (code === 'LOGIN_SUCCESS') {
        log.debug('Single-factor login completed successfully')
        // Simple login completed successfully
        commit('clearUser')
        // type: 'LOGIN' for regular sessions
        commit('setUser', Object.assign(user, { iat, exp, type: 'LOGIN' }))
        commit('setLoading', false)
        dispatch('initializeFeatureFlagClient', user)
        router.push($route.query.redirect || '/')
        Message({
          message: 'Welcome back! Setting up your dashboard...',
          type: 'success',
          center: false,
          duration: 5000,
        })
      } else if (code === 'MFA_STATUS_ENROLL_REQUIRED') {
        // Login redirected for MFA enrollment
        // TODO: Do we need to store the user in Vuex?
        // TODO: This would help restoring the user's MFA enroll session
        commit('setUser', Object.assign(user, { iat, exp, type: 'MFA_ENROLL' }))
        router.push('/mfa/enroll')
      } else if (code === 'MFA_STATUS_ENROLLED') {
        commit('clearUser')
        commit('setUser', Object.assign(user, { iat, exp, type: 'MFA_PENDING' }))
        commit('setLoading', false)
        router.push({ path: '/mfa/checkpoint', query: { redirect: $route.query.redirect } })
      }
    })
    .catch((err) => {
      Raven.captureBreadcrumb({
        message: 'Caught exception in login mutation',
        category: 'auth',
        data: {
          email,
        },
      })
      Raven.captureException(err)
      commit('clearUser')
      commit('setLoading', false)
      commit('setStatus', 'ERR_UNKNOWN')
    })
}

function clearStatus({ commit }) {
  commit('clearStatus')
}

/**
 * Check whether the stored user is still valid.
 * If not, set an inactivity status message and remove
 * the user from state and storage.
 *
 * Always resolves from returned promise, since there
 * is no "failed" state--only a successful check for
 * expiry.
 *
 * Used primarily in:
 *   * Router navigation guards
 *   * Login session management
 *   * Error processing
 *      * Last-effort check when a fatal GQL error happens
 */
function checkExpiration({ dispatch, state }) {
  return new Promise((resolve) => {
    let user
    if (state.user.id) {
      log.debug('Got user from state')
      user = state.user
    } else if (LocalStorageHelper.userExists()) {
      log.warn('Inconsistent auth state: user in local storage, not in state')
      log.debug('Restoring user from localStorage')
      Raven.captureMessage('Inconsistent auth state: user in local storage, not in state')
      user = LocalStorageHelper.getUser()
    } else {
      log.debug('No user in state or localStorage')
      // No user in state
      // continue to next route handler
      return resolve()
    }
    const remaining = user.exp - Date.now()
    log.debug(`Auth session expires in ${Math.round(remaining / 1000)}s`)
    if (remaining < AUTH_EXP_CHECK_INTERVAL) {
      log.debug('Auth session has expired. Triggering inactivity logout')
      dispatch('logOutInactivity')
      return resolve()
    }
    return resolve()
  })
}

/**
 * Submit verification code in exchange
 * for a login session. Carries out the
 * major functions of the login action with
 * some additional handling for error states.
 */
function exchangeMFACode({ commit }, { mfaCode, rememberMe, redirect }) {
  log.debug('Attempting to exchange MFA code for session')
  apollo.defaultClient.mutate({
    mutation: ExchangeMFACode,
    variables: { mfaCode, rememberMe },
  }).then(({ data: { exchangeMFACode: { code, user, iat, exp } } }) => {
    commit('setStatus', code)
    if (code === 'LOGIN_SUCCESS') {
      log.debug('Multi-factor login completed successfully')
      // Simple login completed successfully
      commit('clearUser')
      // type: 'LOGIN' for regular sessions
      commit('setUser', Object.assign(user, { iat, exp, type: 'LOGIN' }))
      log.debug('Redirecting user to dashboard')
      if (redirect) {
        router.push(redirect)
      } else {
        router.push('/')
      }
      return Message({
        message: 'Welcome back! Setting up your dashboard...',
        type: 'success',
        center: false,
        duration: 5000,
      })
    }
    return Message({
      message: 'Incorrect code. Please try again.',
      type: 'error',
      center: false,
    })
  }).catch((err) => {
    Message({
      message: 'An unexpected error occurred. Please try again.',
      type: 'error',
      center: false,
    })
    Raven.captureException(err)
  })
}

/**
 * Remove user and redirect for inactivity
 */
function logOutInactivity({ commit, state }) {
  log.debug('Logging out due to inactivity')
  Raven.captureBreadcrumb({
    message: 'Logged out due to inactivity',
    category: 'auth',
    data: {
      iat: state.user.iat,
      exp: state.user.exp,
    },
  })
  /**
   * It is necessary to clear the user before
   * navigating to the login view. The router
   * guards would enter into an infinite loop
   * if an authenticated user still existed.
  */
  log.debug('Removing user from state and storage. Session manager should stop')
  commit('clearUser')
  commit('setStatus', 'LOGGED_OUT_INACTIVITY')
  log.debug('Redirecting to /login with inactivity message')
  router.push('/login')
}

/**
 * Log out and clear current user from
 * state and localStorage. Also resets the
 * Apollo catch and redirects to the login page.
 */
function logout({ commit, dispatch, state }) {
  Raven.captureBreadcrumb({
    message: 'Dispatched logout action',
  })
  /**
   * Frontend logout happens immediately
   * in order to improve page responsiveness
   * TODO: Review this practice
   */
  router.push('/login')
  dispatch('closeFeatureFlagClient')
  commit('clearUser')
  commit('setStatus', 'LOGGED_OUT')
  commit('setLoading', false)
  apollo.defaultClient.mutate({
    mutation: Logout,
  }).then(() => {
    localStorage.clear()
    apollo.defaultClient.resetStore()
  }).catch((err) => {
    localStorage.clear()
    Raven.captureException(err)
    log.error(err)
    apollo.defaultClient.resetStore()
  })
}

/**
 * Reset a user's password given a new password
 * and a token received by email.
 */
function resetPassword({ commit }, { password, resetToken }) {
  log.debug('Attempting to set a new password')
  apollo.defaultClient.mutate({
    mutation: ResetPassword,
    variables: { password, token: resetToken },
  }).then(({ data: { resetPassword: { success, user, iat, exp, rerouteUrl } } }) => {
    log.debug('Password changed successfully. A login cookie has been set')
    if (success === true) {
      commit('setUser', Object.assign(user, { iat, exp, type: 'LOGIN' }))
      Message({
        message: 'Password changed successfully.',
        type: 'success',
        center: false,
      })

      // Reroute marketers to new CMS
      if (rerouteUrl) {
        window.location = `https://${rerouteUrl}`
      } else {
        router.push('/')
      }
      return
    }
    log.debug('Could not reset password')
    commit('clearUser')
    commit('setStatus', 'PASSWORD_RESET_FAIL')
  }).catch((err) => {
    Raven.captureException(err)
    log.error('Could not set new password:', err)
    commit('clearUser')
    commit('setStatus', 'PASSWORD_RESET_FAIL')
  })
}

function requestPasswordReset({ commit }, { email }) {
  log.debug('Attempting to request a password reset')
  apollo.defaultClient.mutate({
    mutation: RequestPasswordReset,
    variables: { email },
  // eslint-disable-next-line no-shadow
  }).then(({ data: { requestPasswordReset } }) => {
    if (requestPasswordReset) {
      log.debug('Password reset request successful')
      commit('setStatus', 'PASSWORD_RESET_SUCCESS')
      return
    }
    log.debug('Password reset request failed')
    commit('setStatus', 'PASSWORD_RESET_FAIL')
  }).catch((err) => {
    commit('setStatus', 'PASSWORD_RESET_FAIL')
    Raven.captureException(err)
    log.error(err)
  })
}

/**
 * Restore a user from localStorage
 * Redirection is handled by the router
 */
function restoreUser({ commit, state, dispatch }) {
  log.debug('Attempting to restore user from localStorage')
  if (LocalStorageHelper.userExists()) {
    log.debug('A user exists in localStorage. Restoring to state')
    // Restore user to state
    commit('setUser', LocalStorageHelper.getUser())
    // restore feature flag client to state
    dispatch('initializeFeatureFlagClient', LocalStorageHelper.getUser())
    // User is switched
    if (state.user.requestingUser) {
      // Use the original user's identifiers in Sentry
      Raven.setUserContext({
        email: state.user.requestingUser.email,
        id: state.user.requestingUser.id,
      })
      // Manually attach switch information
      Raven.captureBreadcrumb({
        category: 'auth',
        message: `Switch session restored: switched to ${state.user.email} (id:${state.user.id}`,
      })
      // Display a switched status message
      Message({
        message: `Viewing platform as ${state.user.name}`,
        customClass: 'ps-message__userSwitcher',
        duration: 0,
        showClose: true,
        onClose: function restoreOriginalUser() {
          dispatch('restoreOriginalUser', null, { root: true })
        },
      })
    }
  } else {
    log.debug('No user exists in localStorage')
    Raven.captureMessage('No user exists in local storage')
  }
}

/**
 * Initializes the feature flag client for launch darkly
 * LDClient is statically loaded from a minified js file containing the
 * Launch Darkly JS SDK. This was necessary since the library cannot
 * be easily transpiled with webpack 3 and babel 7
 */
function initializeFeatureFlagClient({ commit }, user) {
  // eslint-disable-next-line
  if (LDClient) {
    const context = {
      kind: 'user',
      key: user.id,
      email: user.email,
      company_id: user.companiesId,
      role: user.roles[0],
    }
    log.debug('Initializing feature flag client')
    // eslint-disable-next-line
    const client = LDClient.initialize(process.env.LAUNCH_DARKLY_CLIENT_SIDE_ID, context)
    client.waitForInitialization(5).then(() => {
      log.debug('Successfully Initialized feature flag client')
      commit('setFeatureFlagClient', client)
    }).catch(() => log.debug('Failed to initialize feature flag client'))
  } else {
    log.debug('LD CLient is not defined')
  }
}

function closeFeatureFlagClient({ commit, state }) {
  state.featureFlagClient.close().then(() => {
    log.debug('Succesfully closed the feature flag client')
    commit('clearFeatureFlagClient')
  }).catch(() => {
    log.debug('Error closing feature flag client')
  })
}

export default {
  init,
  initializeFeatureFlagClient,
  checkExpiration,
  clearStatus,
  closeFeatureFlagClient,
  exchangeMFACode,
  login,
  logOutInactivity,
  logout,
  resetPassword,
  requestPasswordReset,
  restoreUser,
}



// WEBPACK FOOTER //
// ./src/store/modules/auth/auth.actions.js